import moment from 'moment';
import {
  ApiTrainingRecord,
  AssignedTrainingsResponse,
  TrainingRecord,
  TrainingCompletionStatus,
  API_STATUS_MAPPING,
  TrainingStatus,
} from './types';
import { ExtendedUser } from '../../../Types';

/**
 * Parses ISO 8601 duration format (e.g., "PT0H0M0S") to minutes
 * @param duration - ISO 8601 duration string
 * @returns Duration in minutes
 */
export const parseDurationToMinutes = (duration: string): number => {
  const momentDuration = moment.duration(duration);

  const hours = momentDuration.hours() ?? 0;
  const minutes = momentDuration.minutes() ?? 0;
  const seconds = momentDuration.seconds() ?? 0;

  // Convert to total minutes (round up seconds to next minute)
  return hours * 60 + minutes + (seconds > 0 ? 1 : 0);
};

/**
 * Maps API status to UI completion status
 * @param apiStatus - Status from API
 * @returns Mapped TrainingCompletionStatus
 */
export const mapApiStatusToCompletionStatus = (
  apiStatus: string
): TrainingCompletionStatus => {
  const mappedStatus = API_STATUS_MAPPING[apiStatus];
  if (!mappedStatus) {
    console.warn(`Unknown API status: ${apiStatus}, defaulting to NOT_STARTED`);
    return TrainingCompletionStatus.NOT_STARTED;
  }
  return mappedStatus;
};

/**
 * Extracts associate ID from user ID
 * @param userId - User ID from API (e.g., "cscentral_na_user:8298731")
 * @returns Associate ID
 */
export const extractAssociateId = (userId: string): string => {
  // Extract the ID part after the colon
  const parts = userId.split(':');
  return parts.length > 1 ? parts[1] : userId;
};

/**
 * Gets the training title from multilingual attributes
 * @param multilingualAttributes - Array of language attributes
 * @returns Training title (defaults to first available or fallback)
 */
export const getTrainingTitle = (multilingualAttributes: any[]): string => {
  if (!multilingualAttributes || multilingualAttributes.length === 0) {
    return 'Unknown Training';
  }

  // Try to find default language first
  const defaultLang = multilingualAttributes.find(
    attr => attr.isDefaultLanguage
  );
  if (defaultLang?.trainingTitle) {
    return defaultLang.trainingTitle;
  }

  // Fall back to first available title
  const firstAvailable = multilingualAttributes.find(
    attr => attr.trainingTitle
  );
  return firstAvailable?.trainingTitle || 'Unknown Training';
};

/**
 * Determines completion date based on status and timestamps
 * @param status - Training status
 * @param latestTranscriptTimestamp - Latest transcript timestamp
 * @returns Completion date or undefined
 */
export const getCompletionDate = (
  status: string,
  latestTranscriptTimestamp?: string
): string | undefined => {
  if (status === TrainingStatus.COMPLETED && latestTranscriptTimestamp) {
    return latestTranscriptTimestamp;
  }
  return undefined;
};

/**
 * Parses a single API training record to UI training record
 * @param apiRecord - API training record
 * @returns Parsed TrainingRecord
 */
export const parseApiTrainingRecord = (
  apiRecord: ApiTrainingRecord,
  profile: ExtendedUser[]
): TrainingRecord => {
  try {
    const {
      assignmentId,
      assignedDate,
      dueDate,
      latestTranscriptTimestamp,
      status,
      trainingCatalog,
      userAlias,
      userId,
    } = apiRecord;

    const {
      deepLink,
      duration,
      multilingualAttributes,
      metrics = [],
    } = trainingCatalog;

    const recordUser = profile.find(p => p.alias === userAlias);

    return {
      id: assignmentId,
      associateName: recordUser?.displayName ?? '',
      associateId: extractAssociateId(userId),
      courseName: getTrainingTitle(multilingualAttributes),
      courseLink: deepLink,
      ccxAttributes:
        metrics.length > 0
          ? metrics
              .sort()
              .map(str => str.charAt(0).toUpperCase() + str.slice(1))
          : ['N/A'],
      completionStatus: mapApiStatusToCompletionStatus(status),
      dueDate,
      duration: parseDurationToMinutes(duration),
      assignedDate,
      completedDate: getCompletionDate(status, latestTranscriptTimestamp),
      latestTranscriptTimestamp,
    };
  } catch (error) {
    console.error('Error parsing API training record:', error, apiRecord);

    // Return a fallback record to prevent crashes
    return {
      id: apiRecord.assignmentId || 'unknown',
      associateName: apiRecord.userAlias || 'Unknown Associate',
      associateId: extractAssociateId(apiRecord.userId || ''),
      courseName: 'Unknown Training',
      courseLink: '',
      ccxAttributes: ['N/A'],
      completionStatus: TrainingCompletionStatus.NOT_STARTED,
      dueDate: apiRecord.dueDate || '',
      duration: 0,
      assignedDate: apiRecord.assignedDate || '',
      completedDate: undefined,
      latestTranscriptTimestamp: apiRecord.latestTranscriptTimestamp,
    };
  }
};

/**
 * Parses the full API response to an array of UI training records
 * @param apiResponse - API assigned trainings response
 * @returns Array of parsed TrainingRecord objects
 */
export const parseAssignedTrainingsResponse = (
  apiResponse: AssignedTrainingsResponse,
  profile: ExtendedUser[]
): TrainingRecord[] => {
  if (!apiResponse?.records || !Array.isArray(apiResponse.records)) {
    return [];
  }

  return apiResponse.records.map(record =>
    parseApiTrainingRecord(record, profile)
  );
};

/**
 * Validates and parses raw API response data
 * @param rawData - Raw data from API call
 * @returns Parsed training records or empty array on error
 */
export const parseTrainingData = (
  rawData: any,
  profile: ExtendedUser[]
): TrainingRecord[] => {
  if (rawData?.records) {
    return parseAssignedTrainingsResponse(rawData, profile);
  }

  return [];
};
